import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Accordion, Popup, Icon, Grid, Menu, Transition } from 'semantic-ui-react'
import { Link } from 'react-router-dom'

import './Sidebar.scss'
import styles from './../../styles/global.module.scss'
import { connect } from 'react-redux'
import driverActions from '../../store/actions/drivers'
import icons from '../Icons'
import { useUniversalLateralMenuHook } from 'hooks/useUniversalLateralMenuHook'
import { isTabSwitchNavigation, useTabNavigation } from 'hooks/useTabNavigation'

function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window
    return { width, height }
}

const isImageIcon = (v) =>
    typeof v === 'string' && (/^https?:\/\//.test(v) || v.startsWith('/') || v.startsWith('data:'))

const normalizeGroup = (s = '') =>
    (s ?? '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()

const stripQueryHash = (s = '') => String(s).split('#')[0].split('?')[0]

const normalizePath = (s = '/') => {
    const clean = stripQueryHash(s || '/').toLowerCase().trim()
    const withSlash = ('/' + clean).replace(/\/+/g, '/')
    return withSlash !== '/' ? withSlash.replace(/\/$/, '') : '/'
}

const stripBasePrefix = (pathname) => {
    const norm = normalizePath(pathname)
    const parts = norm.split('/')
    const baseSet = new Set(['dashboard', 'home', 'inicio'])
    return (parts.length > 2 && baseSet.has(parts[1])) ? '/' + parts.slice(2).join('/') : norm
}
const equalsOrParentOf = (current, target) =>
    current === target || current.startsWith(target + '/')

const VACATION_REQUEST_URL = '/driver/vacation_request'
const VACATION_REQUEST_LIST_URL = '/driver/vacation_request_list'
const isVacationPayrollRoute = (url = '') =>
    [VACATION_REQUEST_URL, VACATION_REQUEST_LIST_URL].includes(String(url))

const payrollModule = {
    'Nómina': {
        routes: [
            { name: 'Solicitud de Vacaciones', url: '/driver/vacation_request_list' },
            { name: 'Solicitud de Horas extras', url: '/driver/extra_hour_request' },
        ],
    },
}

const DriverSidebarMenu = (props) => {
    const { t } = useTranslation()
    const { expand, setExpand, location, mobileOnly, setMobileOnly, driver, lang, onTabOpen } = props

    const [previousExpand, setPreviousExpand] = useState(null)
    // lateralMenu ahora viene del hook con persistencia en backend
    const { lateralMenu } = useUniversalLateralMenuHook('driver');

    // Estado y lógica para la imagen del driver: medida, recorte y src procesado
    const [driverLogoSrc, setDriverLogoSrc] = useState('')
    const [companyLogoSrc, setCompanyLogoSrc] = useState(null)
    const [logoDimensions, setLogoDimensions] = useState({
        menu: null,
        default: null
    })
    const [imgNatural, setImgNatural] = useState({ width: 0, height: 0 })
    const [imgCropped, setImgCropped] = useState(false)

    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions())
    const [openHeaderIndices, setOpenHeaderIndices] = useState([])
    const [openSubHeaderTitles, setOpenSubHeaderTitles] = useState([])
    const [availableRoutes, setAvailableRoutes] = useState([])
    const [allowedRoutes, setAllowedRoutes] = useState([])

    const underlineMenu = false // ajusta si usas esta bandera en tu css

    const companyLogoFrameRef = useRef(null)

    const getLogoFitScore = (naturalWidth, naturalHeight, frameWidth, frameHeight) => {
        if (!naturalWidth || !naturalHeight || !frameWidth || !frameHeight) return -1

        const containScale = Math.min(frameWidth / naturalWidth, frameHeight / naturalHeight)
        const renderedWidth = naturalWidth * containScale
        const renderedHeight = naturalHeight * containScale
        const widthRatio = renderedWidth / frameWidth
        const heightRatio = renderedHeight / frameHeight
        const usedRatio = Math.max(widthRatio, heightRatio)

        const verticalPenalty = heightRatio > 0.94 ? (heightRatio - 0.94) * 0.15 : 0
        const compactPenalty = widthRatio < 0.56 ? (0.56 - widthRatio) * 0.35 : 0
        return usedRatio - verticalPenalty - compactPenalty
    }

    const medirLogo = (tipo, img) => {
        if (!img?.naturalWidth || !companyLogoFrameRef?.current) return

        const frameEl = companyLogoFrameRef.current
        const frameWidth = frameEl.clientWidth || 0
        const frameHeight = frameEl.clientHeight || 0

        if (!frameWidth || !frameHeight) return

        setLogoDimensions(prev => ({
            ...prev,
            [tipo]: {
                width: img.naturalWidth,
                height: img.naturalHeight
            }
        }))
    }

    const marcarError = (tipo) => {
        setLogoDimensions(prev => ({
            ...prev,
            [tipo]: { width: 0, height: 0 }
        }))
    }

    const activeItemRef = useRef(null)
    const containerRef = useRef(null)
    const manualToggleForPath = useRef(null)

    // mapa iconos por grupo (Icons.js)
const groupIconMap = useMemo(() => {
        const map = new Map()
            ; (icons || []).forEach((i) => map.set(normalizeGroup(i.title), i.url))
        return map
    }, [])

    const { tabs: openTabs, activeTabId } = useTabNavigation();

    useEffect(() => {
        componentDidMount()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const componentDidMount = async () => {
        try {
            const driverId = props.driver?.id
            const promises = [props.getDriverModules()]
            promises.push(driverId ? props.getWebDriverPermissions(driverId) : Promise.resolve([]))
            const [av, allowed] = await Promise.all(promises)
            const available = Array.isArray(av) ? [...av] : []
            const hasPayroll = available.some((m) => {
                const moduleName = String(Object.keys(m || {})[0] || '').toLowerCase()
                return moduleName.includes('nómina') || moduleName.includes('nomina') || moduleName.includes('payroll')
            })
            if (!hasPayroll) available.push(payrollModule)

            setAvailableRoutes(available)
            setAllowedRoutes(allowed || [])
        } catch (err) {
            console.error('DriverSidebarMenu load error', err)
            setAvailableRoutes([])
            setAllowedRoutes([])
        }
    }

    // Mantener el src inicial cuando cambia el driver
    useEffect(() => {
        const menuLogo = driver?.sys_company_data?.menu_logo_url || null
        const defaultLogo = driver?.sys_company_data?.logo_url || null

        if (!menuLogo && !defaultLogo) {
            setCompanyLogoSrc(null)
            return
        }

        if (!menuLogo) {
            setCompanyLogoSrc(defaultLogo)
            return
        }

        if (!defaultLogo) {
            setCompanyLogoSrc(menuLogo)
            return
        }

        if (!logoDimensions.menu || !logoDimensions.default) {
            setCompanyLogoSrc(menuLogo)
            return
        }

        const frameEl = companyLogoFrameRef?.current
        const frameWidth = frameEl?.clientWidth || 0
        const frameHeight = frameEl?.clientHeight || 0

        if (!frameWidth || !frameHeight) {
            setCompanyLogoSrc(menuLogo)
            return
        }

        const scoreMenu = getLogoFitScore(
            logoDimensions.menu.width,
            logoDimensions.menu.height,
            frameWidth,
            frameHeight
        )

        const scoreDefault = getLogoFitScore(
            logoDimensions.default.width,
            logoDimensions.default.height,
            frameWidth,
            frameHeight
        )

        setCompanyLogoSrc(scoreMenu >= scoreDefault ? menuLogo : defaultLogo)
    }, [
        driver?.sys_company_data?.menu_logo_url,
        driver?.sys_company_data?.logo_url,
        logoDimensions,
        expand,
    ])

    // Sincronizar lateralMenu con expand en TIEMPO REAL
    useEffect(() => {
        if (mobileOnly || lateralMenu === null) return;
        if (lateralMenu === true) {
            setExpand(1);
        } else if (lateralMenu === false) {
            if (typeof onTabOpen !== 'function') setExpand(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lateralMenu]);

    // resize
    useEffect(() => {
        function handleResize() {
            const dims = getWindowDimensions()
            if (dims.width !== windowDimensions.width || dims.height !== windowDimensions.height) {
                setWindowDimensions(dims)
            }
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [windowDimensions])

    // mobile behavior
    useEffect(() => {
        if (windowDimensions.width <= 425) {
            setMobileOnly(true)
            if (expand === 1 || expand == null) setExpand(1)
        } else {
            setMobileOnly(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowDimensions])
    // mantener sidebar abierto al navegar; en móvil asegurar visible
    useEffect(() => {
        if (!location) return
        if (mobileOnly) setExpand(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, mobileOnly])

    // construir módulos filtrados
    const modulesToRender = useMemo(() => {
        if (!availableRoutes || !availableRoutes.length) return []
        return availableRoutes
            .filter((module) => {
                const moduleName = Object.keys(module)[0]
                const submodules = module[moduleName].routes || []
                return submodules.some((route) => allowedRoutes.includes(route.url))
            })
            .map((module) => {
                const moduleName = Object.keys(module)[0]
                const data = module[moduleName] || {}
                return {
                    module_group: moduleName,
                    module_group_en: data.name_en,
                    module_group_pt: data.name_pt,
                    module_group_ar: data.name_ar,
                    icon: data.icon,
                    routes: (data.routes || [])
                        .filter((r) => allowedRoutes.includes(r.url))
                        .map((r) => ({
                            ...r,
                            sub_group: r.sub_group ?? r.subgroup ?? null,
                        })),
                }
            })
    }, [availableRoutes, allowedRoutes])

    // construir subHeaders (agrupados por sub_group dentro de cada módulo)
    const subHeaders = useMemo(() => {
        const result = []
        modulesToRender.forEach((module) => {
            (module.routes || []).forEach((route) => {
                if (route.sub_group) {
                    const exists = result.find(
                        (item) => item?.sub_group === route.sub_group && item?.group === module.module_group
                    )
                    if (!exists) {
                        result.push({
                            title: route.sub_group,
                            name_en: route.sub_group_en || route.name_en,
                            name_pt: route.sub_group_pt || route.name_pt,
                            name_ar: route.sub_group_ar || route.name_ar,
                            group: module.module_group,
                            sub_group: route.sub_group,
                            name: route.sub_group_name ?? route.sub_group,
                        })
                    }
                }
            })
        })
        return result
    }, [modulesToRender])
    // path actual (soporta HashRouter)
    const rawPath = (typeof location?.hash === 'string' && location.hash.startsWith('#'))
        ? location.hash.slice(1)
        : (location?.pathname || window.location.pathname || '/')
    const currentPath = normalizePath(rawPath)
    const appPath = stripBasePrefix(rawPath)
    const shouldPreserveSidebarState = isTabSwitchNavigation(location)

const isActiveRoute = (to) => {
        if (!to) return false
        const target = normalizePath(`/${to}`)
        return equalsOrParentOf(appPath, target) || equalsOrParentOf(currentPath, target)
    }
    const normalizeTabPath = (path) => {
        const normalized = `/${path || ''}`.replace(/\/+/g, '/')
        return normalized === '/' ? '/' : normalized.replace(/\/$/, '')
    }
    const isRouteInOpenTab = (routePath) => {
        if (!openTabs || !Array.isArray(openTabs)) return false
        const normalized = normalizeTabPath(routePath)
        return openTabs.some(tab => normalizeTabPath(tab.path) === normalized)
    }
    const isRouteInActiveTab = (routePath) => {
        if (!openTabs || !activeTabId) return false
        const normalized = normalizeTabPath(routePath)
        const activeTab = openTabs.find(tab => tab.id === activeTabId)
        return activeTab && normalizeTabPath(activeTab.path) === normalized
    }

    const getDisplayRoutes = (moduleGroup, routes = []) => {
        const moduleKey = normalizeGroup(moduleGroup)
        if (!moduleKey.includes('nomina') && !moduleKey.includes('payroll')) return routes

        const vacationRoutes = routes.filter((route) => isVacationPayrollRoute(route.url))
        const otherRoutes = routes.filter((route) => !isVacationPayrollRoute(route.url))
        if (!vacationRoutes.length) return otherRoutes

        const preferredVacationRoute =
            vacationRoutes.find((route) => route.url === VACATION_REQUEST_LIST_URL) || vacationRoutes[0]

        return [
            {
                ...preferredVacationRoute,
                name: 'Solicitud de Vacaciones',
                url: VACATION_REQUEST_LIST_URL,
            },
            ...otherRoutes,
        ]
    }

    const routes = useMemo(() => modulesToRender.flatMap((m) => (m.routes || []).map((r) => ({ ...r, group: m.module_group }))), [modulesToRender])
    // detectar ruta activa con memoización
    const activeRoute = useMemo(() => routes.find((r) => isActiveRoute(r.url)), [routes, currentPath])
    const activeHeaderIndex = useMemo(() => {
        if (!activeRoute) return -1
        return modulesToRender.findIndex((h) => h.module_group === activeRoute.group)
    }, [modulesToRender, activeRoute])
const activeSubGroup = activeRoute?.sub_group ?? null
    const activeGroup = activeRoute?.group || null


    // auto-open headers cuando está pineado (respeta toggle manual)
    useEffect(() => {
        if (!lateralMenu) return
        if (shouldPreserveSidebarState) return
        if (!routes?.length || !modulesToRender?.length) return
        if (manualToggleForPath.current === currentPath) {
            manualToggleForPath.current = null
            return
        }
        if (activeHeaderIndex >= 0) {
            setOpenHeaderIndices(prev => (prev.includes(activeHeaderIndex) ? prev : [...prev, activeHeaderIndex]))
        }
        if (activeSubGroup) {
            setOpenSubHeaderTitles(prev => (prev.includes(activeSubGroup) ? prev : [...prev, activeSubGroup]))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPath, activeHeaderIndex, activeSubGroup, lateralMenu, shouldPreserveSidebarState])

    // scroll to active item
    useEffect(() => {
        if (shouldPreserveSidebarState) return
        if (!activeRoute) return
        const t = setTimeout(() => {
            const el = activeItemRef.current
            const container = containerRef.current
            if (!el || !container) return
            try {
                const elRect = el.getBoundingClientRect()
                const contRect = container.getBoundingClientRect()
                if (elRect.top < contRect.top || elRect.bottom > contRect.bottom) {
                    const offset = el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2
                    container.scrollTo({ top: Math.max(0, offset), behavior: 'auto' })
                }
            } catch (e) {
                try { el.scrollIntoView({ block: 'nearest', behavior: 'auto' }) } catch { el.scrollIntoView() }
            }
        }, 260)
        return () => clearTimeout(t)
    }, [currentPath, expand, activeRoute, shouldPreserveSidebarState])

    // Manejo cuando se carga la imagen: medir y procesar según reglas
    const handleCompanyImageLoad = async (e) => {
        try {
            const img = e.target
            const nw = img.naturalWidth || 0
            const nh = img.naturalHeight || 0
            setImgNatural({ width: nw, height: nh })

            const MAX_SIDE = 1500
            const MIN_SIDE = 500
            const TARGET_WIDTH = 1000
            const TARGET_HEIGHT = 700
            const maxSide = Math.max(nw, nh)

            // Si es muy grande (>1500px): redimensionar proporcionalmente a 1000x700
            if (maxSide > MAX_SIDE || nw > MAX_SIDE || nh > MAX_SIDE) {
                try {
                    const canvas = document.createElement('canvas')
                    canvas.width = TARGET_WIDTH
                    canvas.height = TARGET_HEIGHT
                    const ctx = canvas.getContext('2d')

                    // Calcular escalado manteniendo aspect ratio
                    const scale = Math.min(TARGET_WIDTH / nw, TARGET_HEIGHT / nh)
                    const scaledW = nw * scale
                    const scaledH = nh * scale
                    const dx = (TARGET_WIDTH - scaledW) / 2
                    const dy = (TARGET_HEIGHT - scaledH) / 2

                    ctx.clearRect(0, 0, TARGET_WIDTH, TARGET_HEIGHT)
                    ctx.drawImage(img, 0, 0, nw, nh, dx, dy, scaledW, scaledH)
                    const dataUrl = canvas.toDataURL('image/png')
                    setDriverLogoSrc(dataUrl)
                    setImgCropped(true)
                    return
                } catch (err) {
                    setImgCropped(false)
                }
            }

            // Si es muy pequeña (<500px): escalar a 1000x700
            if (maxSide > 0 && (maxSide < MIN_SIDE || nw < MIN_SIDE || nh < MIN_SIDE)) {
                try {
                    const canvas = document.createElement('canvas')
                    canvas.width = TARGET_WIDTH
                    canvas.height = TARGET_HEIGHT
                    const ctx = canvas.getContext('2d')

                    // Calcular escalado manteniendo aspect ratio
                    const scale = Math.min(TARGET_WIDTH / nw, TARGET_HEIGHT / nh)
                    const scaledW = nw * scale
                    const scaledH = nh * scale
                    const dx = (TARGET_WIDTH - scaledW) / 2
                    const dy = (TARGET_HEIGHT - scaledH) / 2

                    ctx.clearRect(0, 0, TARGET_WIDTH, TARGET_HEIGHT)
                    ctx.drawImage(img, 0, 0, nw, nh, dx, dy, scaledW, scaledH)
                    const dataUrl = canvas.toDataURL('image/png')
                    setDriverLogoSrc(dataUrl)
                    setImgCropped(false)
                    return
                } catch (err) {
                    // Si falla no procesamos
                }
            }

            // Caso intermedio: mostrar tal cual
            setImgCropped(false)
        } catch (err) {
            // ignore
        }
    }

    // Escalado visual para el logo
    const logoScale = useMemo(() => {
        if (!imgNatural.width || !imgNatural.height) return 1.15
        const maxSide = Math.max(imgNatural.width, imgNatural.height)
        if (maxSide <= 100) return 2.6
        if (maxSide <= 150) return 2.2
        if (maxSide <= 400) return 2.0
        if (maxSide <= 800) return 1.9
        if (maxSide <= 1000) return 1.6
        const ratio = imgNatural.width / imgNatural.height
        if (ratio >= 2) return 1.45
        if (ratio > 1.2) return 1.25
        return 1.15
    }, [imgNatural])

    // Estilos dinámicos para centrar/escalar la imagen
    const companyImgStyle = useMemo(() => ({
        maxWidth: '100%',
        maxHeight: '100%',
        width: imgNatural.width && imgNatural.width > imgNatural.height ? '100%' : 'auto',
        height: imgNatural.height && imgNatural.height > imgNatural.width ? '100%' : 'auto',
        objectFit: imgCropped ? 'cover' : 'contain',
        display: 'block',
        cursor: 'pointer'
    }), [imgNatural, imgCropped])

    const handleHeaderClick = useCallback((e, titleObj) => {
        e.preventDefault(); e.stopPropagation()
        const index = titleObj.index ?? titleObj
        manualToggleForPath.current = currentPath
        setOpenHeaderIndices((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]))
    }, [currentPath])
    const handleSubHeaderClick = useCallback((e, titleObj) => {
        e.preventDefault(); e.stopPropagation()
        const key = titleObj?.title ?? titleObj
        setOpenSubHeaderTitles((prev) => (prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]))
    }, [])

    const handleTabOpen = useCallback((e, route) => {
        if (typeof onTabOpen === 'function') {
            e.preventDefault()
            onTabOpen(route)
        }
    }, [onTabOpen])

    // Devuelve clase de profundidad para el header (igual que en ClientSidebarMenu)
    const getHeaderDepthClass = (headerIndex, moduleGroup) =>
        openHeaderIndices.includes(headerIndex) ? 'depth-parent' : ''

    return (
        <div
            className={`sidebar`}
            ref={containerRef}
            style={{
                width: (expand === null || expand === 1) ? '225px' : '0px',
                overflowY: 'auto',
                overflowX: 'hidden',
            }}
        >
            {(expand === null || expand === 1) && (
                <div className='Sidebar__CloseStickyy' style={{ padding: 0, margin: 0 }}>
                    <div className='Sidebar__Header'>
{/* Fila 1: Solo botón hamburguesa */}
                        <div className='Sidebar__MenuButtonRow' style={{justifyContent:'flex-start',paddingLeft:'0',paddingRight:'0'}}>
                            <button
                                className='Sidebar__MenuButton Sidebar__MenuButton--shell'
                                onClick={() => {
                                    if (lateralMenu && !mobileOnly) {
                                        return
                                    }

                                    setExpand((previousExpandedState) => {
                                        setPreviousExpand(previousExpandedState)
                                        const newExpandState = !mobileOnly
                                            ? previousExpandedState == 1
                                                ? (previousExpand == null || previousExpand == 2) ? 0 : 2
                                                : 1
                                            : !previousExpandedState ? 1 : 0

                                        return newExpandState
                                    })
                                }}
                                aria-label={t('Abrir/cerrar menú lateral')}
                                style={{background:'transparent',border:'1.1px solid #6c6c6c',borderRadius:7,padding:'0 13px 0 3px',height:26,minWidth:62,display:'flex',alignItems:'center',gap:3,boxShadow:'none',justifyContent:'flex-start',textAlign:'left'}}
                            >
                                <img
                                    src='https://img.icons8.com/?size=100&id=100033&format=png&color=000000'
                                    alt='Menu'
                                    style={{width:14.5,height:14.5,marginRight:-6,marginLeft:0,display:'inline-block'}}
                                />
                                <span style={{fontSize:13,color:'#6c6c6c',fontWeight:500,whiteSpace:'nowrap',marginLeft:0}}>ocultar</span>
                            </button>
                        </div>

                        {/* Fila 2: Logo */}
                        <div className='Sidebar__LogoRow'>
                            <div className='Sidebar__LogoSlot'>
                                <div className='Sidebar__LogoFrame' ref={companyLogoFrameRef}>
                                    {/* Logos ocultos para medir */}
                                    {driver?.sys_company_data?.menu_logo_url && (
                                        <img
                                            src={driver.sys_company_data.menu_logo_url}
                                            style={{ display: 'none' }}
                                            onLoad={(e) => medirLogo('menu', e.target)}
                                            onError={() => marcarError('menu')}
                                        />
                                    )}
                                    {driver?.sys_company_data?.logo_url && (
                                        <img
                                            src={driver.sys_company_data.logo_url}
                                            style={{ display: 'none' }}
                                            onLoad={(e) => medirLogo('default', e.target)}
                                            onError={() => marcarError('default')}
                                        />
                                    )}

                                    {/* Logo visible */}
                                    {companyLogoSrc ? (
                                        <img
                                            src={companyLogoSrc}
                                            alt={t('Company Logo')}
                                            className='Sidebar__CompanyLogo SideBar__Outline'
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        />
                                    ) : (
                                        <div className='Sidebar__LogoPlaceholder' />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Transition visible={expand === null || expand === 1} animation='fade down' duration={220} unmountOnHide={false}>
                <div style={{ marginTop: '10px', paddingTop: '10px' }}>
                    {modulesToRender.map((header, index) => {
                        const isOpen = openHeaderIndices.includes(index)
                        const displayRoutes = getDisplayRoutes(header.module_group, header.routes || [])
                        return (
                            <Accordion key={`accordion-${header.module_group}-${index}`} fluid style={{ paddingLeft: 12, paddingBottom: 5 }}>
                                <Accordion.Title
                                    key={`title-${header.module_group}-${index}`}
                                    active={isOpen}
                                    index={index}
                                    onClick={(e, t) => handleHeaderClick(e, { index })}
                                    className={`${getHeaderDepthClass(index, header.module_group)}`}
                                >
<Menu.Item className={`${styles.shrinkOnHover} SubMenu__Item ${underlineMenu ? 'lighten-hover-border' : ''}${activeGroup === header.module_group ? ' is-active' : ''}`}
                                        //className={`${styles.shrinkOnHover} ${underlineMenu ? 'lighten-hover-border' : ''}`} 
                                        //className={`${styles.shrinkOnHover} SubMenu__Item ${underlineMenu ? 'lighten-hover-border' : ''}`}
                                        style={{ width: '100%', display: 'inline-block' }}>
                                        {/* Icon único (animado) */}
                                        <Icon
                                            name='dropdown'
                                            style={{
                                                marginRight: 6,
                                                transform: openHeaderIndices.includes(index) ? 'rotate(90deg)' : 'rotate(0deg)',
                                                opacity: openHeaderIndices.includes(index) ? 1 : 0,
                                                transition: 'opacity 600ms cubic-bezier(.2,.8,.2,1), transform 600ms cubic-bezier(.2,.8,.2,1)',
                                                pointerEvents: 'none',
                                            }}
                                        />
                                        {(() => {
                                            const key = normalizeGroup(header.module_group)
                                            const iconValue = groupIconMap.get(key) ?? header.icon ?? 'archive'
                                            return isImageIcon(iconValue) ? (
                                                <img src={iconValue} alt={header.module_group} style={{ width: 24, height: 24, verticalAlign: 'middle', marginRight: 8 }} className='SideBar__Outline' />
                                            ) : (
                                                <Icon name={iconValue} size='large' className='SideBar__Outline' />
                                            )
                                        })()}
                                        {(expand === null || expand === 1) && (
                                            <h5
                                                className={`menu-row-title ${getHeaderDepthClass(index)}`}
                                                style={{ margin: 0 }}
                                            >                                            {t(header.module_group)}
                                            </h5>
                                        )}
                                    </Menu.Item>
                                </Accordion.Title>

                                <Transition visible={isOpen} animation='fade down' duration={220} unmountOnHide={false}>
                                    <Accordion.Content key={`content-${header.module_group}-${index}`} active={isOpen} style={{ marginTop: 0, paddingTop: 0 }}>
                                        <Grid divided='vertically' style={{ width: '100%' }}>
                                            <Grid.Row style={{ paddingBottom: 0 }} />
                                            <Grid.Row style={{ paddingTop: 0, paddingBottom: 0 }}>
                                                <Grid.Column>
                                                    {/* Renderizar subheaders si existen */}
                                                    {subHeaders.map((subHeader, indexSH) => {
                                                        if (subHeader.group !== header.module_group) return null
                                                        const isSubOpen = openSubHeaderTitles.includes(subHeader.title)
                                                        return (
                                                            <Accordion
                                                                key={`sub-accordion-${subHeader.title}-${indexSH}`}
                                                                fluid
                                                                style={{ marginTop: 0, paddingTop: 0, paddingLeft: '18px' }}
                                                            >
                                                                <Accordion.Title
                                                                    active={isSubOpen}
                                                                    onClick={(e) => handleSubHeaderClick(e, { title: subHeader.title })}
                                                                    style={{ marginBottom: 0, paddingBottom: 0 }}
                                                                >
                                                                    <Menu.Item className={`${styles.shrinkOnHover} SubMenu__Item ${underlineMenu ? 'lighten-hover-border' : ''}`} style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                                                                        <Icon
                                                                            name='dropdown'
                                                                            style={{
                                                                                marginRight: 6,
                                                                                transform: isSubOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                                                                opacity: isSubOpen ? 1 : 0,
                                                                                transition: 'opacity 220ms cubic-bezier(.2,.8,.2,1), transform 220ms cubic-bezier(.2,.8,.2,1)',
                                                                                pointerEvents: 'none',
                                                                            }}
                                                                        />
                                                                        <h5 style={{ margin: 0, marginLeft: 6 }}>
                                                                            {t(subHeader.name)}
                                                                        </h5>
                                                                    </Menu.Item>
                                                                </Accordion.Title>
                                                                <Transition visible={isSubOpen} animation='fade down' duration={220} unmountOnHide={false}>
                                                                    <Accordion.Content
                                                                        active={isSubOpen}
                                                                        style={{ marginTop: 0, paddingTop: 0 }}
                                                                    >
                                                                        <Menu vertical fluid text style={{ paddingLeft: '30px' }}>
                                                                            {(displayRoutes || []).map((route, indexR) => {
                                                                                if (route.sub_group !== subHeader.title) return null
                                                                                const active = isActiveRoute(route.url)
                                                                                return (
                                                                                    <Link
                                                                                        key={`link-sub-${indexR}-${route.url}`}
                                                                                        to={route.url}
                                                                                        className={`sub_menu_row ${isRouteInActiveTab(route.url) ? 'is-active' : ''} ${isRouteInOpenTab(route.url) && !isRouteInActiveTab(route.url) ? 'has-tab-open' : ''}`}
                                                                                        onClick={(e) => handleTabOpen(e, route)}
                                                                                    >
                                                                                        <Menu.Item className={styles.growOnHover} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                                                                                            {route.icon ? <Icon name={route.icon} size='small' style={{ marginRight: 8 }} /> : null}
                                                                                            <h5
                                                                                                ref={(el) => { if (active) activeItemRef.current = el }}
                                                                                                className={`menu-item-label${active ? ' is-active' : ''}`}
                                                                                                style={{ marginLeft: 8 }}
                                                                                            >
                                                                                                {t(route.name)}
                                                                                            </h5>
                                                                                        </Menu.Item>
                                                                                    </Link>
                                                                                )
                                                                            })}
                                                                        </Menu>
                                                                    </Accordion.Content>
                                                                </Transition>
                                                            </Accordion>
                                                        )
                                                    })}
                                                    {/* Rutas sin subgrupo */}
                                                    <Menu vertical fluid text style={{ paddingLeft: '30px' }}>
                                                        {(displayRoutes || []).map((route, indexR) => {
                                                            if (route.sub_group) return null
                                                            const active = isActiveRoute(route.url)
                                                            return (
                                                                <Link
                                                                    key={`link-${index}-${indexR}`}
                                                                    to={route.url}
                                                                    className={`sub_menu_row ${isRouteInActiveTab(route.url) ? 'is-active' : ''} ${isRouteInOpenTab(route.url) && !isRouteInActiveTab(route.url) ? 'has-tab-open' : ''} ${isOpen ? 'depth-parent' : ''}`}
                                                                    onClick={(e) => handleTabOpen(e, route)}
                                                                >
                                                                    <Menu.Item className={styles.growOnHover} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                                                                        {route.icon ? <Icon name={route.icon} size='small' style={{ marginRight: 8 }} /> : null}
                                                                        <h5
                                                                            ref={(el) => { if (active) activeItemRef.current = el }}
                                                                            className={`menu-item-label${active ? ' is-active' : ''}`}
                                                                            style={{ marginLeft: 10 }}
                                                                        >
                                                                            {t(route.name)}
                                                                        </h5>
                                                                    </Menu.Item>
                                                                </Link>
                                                            )
                                                        })}
                                                    </Menu>
                                                </Grid.Column>
                                            </Grid.Row>
                                        </Grid>
                                    </Accordion.Content>
                                </Transition>
                            </Accordion>
                        )
                    })}
                </div>
            </Transition>
        </div>
    )
}

const mapStateToProps = (state) => ({
    driver: state.app.driver,
    lang: state.app.lang, // Añadido lang
})

const mapDispatchToProps = (dispatch) => ({
    getDriverModules: () => dispatch(driverActions.getDriverModules()),
    getWebDriverPermissions: (driverId) => dispatch(driverActions.getWebDriverPermissions(driverId)),
})

export default connect(mapStateToProps, mapDispatchToProps)(DriverSidebarMenu)
// ...existing code...



